import type { ApiProblem, StoreApi } from './index';
import type { components } from './generated/store';

type Schemas = components['schemas'];
export type OrderSummary = Schemas['OrderSummaryDto'];
export type OrderDetail = Schemas['OrderDetailDto'];
export type OrderLine = Schemas['OrderLineDto'];
export type OrderEvent = Schemas['OrderEventDto'];
export type Delivery = Schemas['DeliveryDto'];
export type Payment = Schemas['PaymentDto'];
export type OrderSetup = Schemas['PosSetupLists'];
export type OrderMenu = Schemas['OrderMenuDto'];
export type MenuItem = Schemas['MenuItemDto'];
export type CustomerInfo = Schemas['CustomerInfo'];
export type DeliveryDriver = Schemas['DeliveryDriverDto'];
export type KnownCustomer = Schemas['CustomerSnapshot'];
export type KnownAddress = Schemas['CustomerAddressSnapshot'];
export type LineRequest = Schemas['LineRequest'];
export type PaymentTransaction = Schemas['PaymentTransactionDto'];
export type PaymentSummary = Schemas['PaymentProviderSummary'];
export type ShippingTransaction = Schemas['ShippingTransactionDto'];
export type MemberSummary = Schemas['MemberSummary'];
export type OrderMember = Schemas['OrderMemberDto'];
export type MemberEnrolRequest = Schemas['MemberEnrolRequest'];
export type ShippingSummary = Schemas['ShippingProviderSummary'];
export type PaymentDetail = Schemas['PaymentDetailDto'];
export type ShippingDetail = Schemas['ShippingDetailDto'];
/** The e-invoice HO issued for a paid order (ADR 0048, FR-EIN-04). */
export type OrderEinvoice = Schemas['EinvoiceOrderStatus'];
export type PosConfig = Schemas['PosConfigDto'];
export type PosMenu = Schemas['PosMenuDto'];
export type PosMenuItem = Schemas['PosMenuItemDto'];
export type PosMenuDeal = Schemas['PosMenuDealDto'];
export type PosMenuGroup = Schemas['PosMenuGroupDto'];
export type PosMenuGroupMember = Schemas['PosMenuGroupMemberDto'];
export type PosDealComponent = Schemas['PosDealComponentDto'];
export type PosDealChoice = Schemas['PosDealChoiceDto'];
export type PosModifierGroup = Schemas['MenuGroupDto'];
export type DealRequest = Schemas['DealRequest'];
export type DealChoiceRequest = Schemas['DealChoiceRequest'];
export type DealQuote = Schemas['DealQuoteDto'];
export type PosMenuPage = Schemas['PosMenuPageDto'];
export type PosButton = Schemas['PosButtonConfig'];
export type PosSection = Schemas['PosSectionConfig'];
export type PosSaleType = Schemas['PosSaleTypeConfig'];
export type InvoiceInfo = Schemas['InvoiceInfo'];
export type DeliveryRequest = Schemas['CreateDeliveryRequest'];
export type DeliveryInfo = Schemas['DeliveryDto'];
export type DeliveryService = Schemas['DeliveryServiceDto'];
export type DeliveryQuote = Schemas['DeliveryQuoteDto'];
export type CreateDeliveryBody = Schemas['CreateDeliveryRequest'];
export type TerminalInfo = Schemas['TerminalInfo'];
export type SplitBillPart = Schemas['SplitBillPart'];
export type SplitBillResult = Schemas['SplitBillDto'];
/** A QR or wallet payment through HO's payment hub (ADR 0074). */
export type GatewayPayment = Schemas['GatewayPaymentDto'];

export interface TransactionFilters {
  from?: string;
  to?: string;
  storeId?: string;
  provider?: string;
  status?: string;
  search?: string;
  page: number;
  pageSize: number;
}

export interface OrderListParams {
  search?: string;
  /** The order's own status (ADR 0088): `open` (or `active`), `completed`, `voided`, `cancelled`, `merged`. */
  status?: string;
  /** `unpaid`, `partially_paid`, `paid` or `overpaid`. */
  payment?: string;
  /** `not_sent`, `partially_sent`, `sent`… */
  kitchen?: string;
  /** `waiting`, `ready`, `out_for_delivery`, `handed_over`, `delivered`, `served`, `failed`. */
  fulfilment?: string;
  saleType?: string;
  storeId?: string;
  from?: string;
  to?: string;
  /** One business day's orders in every status: the POS's "All today" (the store's open day). */
  businessDayId?: string;
  /** `yes`: orders with a member; `no`: without (ADR 0044). */
  member?: string;
  /** One member's orders (their account at HO). */
  memberId?: string;
  page: number;
  pageSize: number;
}

/** Thrown by every call that fails: the backend's problem with its status. */
function unwrap<T>({ data, error, response }: { data?: T; error?: unknown; response: Response }): T {
  if (!response.ok) {
    const problem = (error ?? {}) as ApiProblem;
    throw { ...problem, status: problem.status ?? response.status } satisfies ApiProblem;
  }
  return data as T;
}

const version = (v: number | string) => ({ 'If-Match': `"${String(v)}"` });

/** A new idempotency key per attempt of a user action (system rule 7). */
export const newKey = () => crypto.randomUUID();

/**
 * Order and payment calls for the POS and the admin portals. Reads are the same at the store and at HO; changes exist
 * only at the store, where the order lives (system rule 4). Changes send the order version the user saw.
 */
export function createOrdersApi(client: object) {
  const api = client as StoreApi;
  const order = (id: string) => ({ params: { path: { id } } });
  return {
    list: async ({ page, pageSize, ...filters }: OrderListParams) => {
      const result = unwrap(
        await api.GET('/api/v1/ordering/orders', { params: { query: { ...filters, page, pageSize } } }),
      );
      return { items: result.items, total: Number(result.total), totalCapped: result.totalCapped ?? false };
    },
    get: async (id: string) => unwrap(await api.GET('/api/v1/ordering/orders/{id}', order(id))),
    events: async (id: string) => unwrap(await api.GET('/api/v1/ordering/orders/{id}/events', order(id))),
    /** Asks HO through the store (3 seconds); fails with sync.ho_unavailable without HO. */
    einvoice: async (id: string) => unwrap(await api.GET('/api/v1/ordering/orders/{id}/einvoice', order(id))),
    deliveries: async (id: string) => unwrap(await api.GET('/api/v1/ordering/orders/{id}/deliveries', order(id))),
    payments: async (orderId: string) =>
      unwrap(await api.GET('/api/v1/payments/orders/{orderId}/payments', { params: { path: { orderId } } })),
    setup: async () => unwrap(await api.GET('/api/v1/ordering/setup')),
    posConfig: async () => unwrap(await api.GET('/api/v1/ordering/pos/config')),
    /** The section's menu with its layout; an open order's own menu wins (ADR 0017). */
    posMenu: async (saleTypeId: string, sectionId?: string | null, menuId?: string | null) =>
      unwrap(
        await api.GET('/api/v1/ordering/pos/menu', {
          params: { query: { saleTypeId, sectionId: sectionId ?? undefined, menuId: menuId ?? undefined } },
        }),
      ),
    posTerminals: async () => unwrap(await api.GET('/api/v1/devices/pos-terminals')),
    paymentTransactions: async (
      query: TransactionFilters & { kind?: string; methodType?: string; paymentMethodId?: string },
    ) => {
      const page = unwrap(await api.GET('/api/v1/payments/transactions', { params: { query } }));
      return { ...page, total: Number(page.total) };
    },
    payment: async (id: string) =>
      unwrap(await api.GET('/api/v1/payments/payments/{id}', { params: { path: { id } } })),
    delivery: async (id: string) =>
      unwrap(await api.GET('/api/v1/ordering/deliveries/{id}', { params: { path: { id } } })),
    paymentProviders: async () => unwrap(await api.GET('/api/v1/payments/providers')),
    shippingTransactions: async (query: TransactionFilters) => {
      const page = unwrap(await api.GET('/api/v1/ordering/deliveries', { params: { query } }));
      return { ...page, total: Number(page.total) };
    },
    shippingProviders: async () => unwrap(await api.GET('/api/v1/ordering/shipping-providers')),
    menu: async (saleTypeId: string) =>
      unwrap(await api.GET('/api/v1/ordering/menu', { params: { query: { saleTypeId } } })),

    create: async (body: Schemas['CreateOrderRequest']) => unwrap(await api.POST('/api/v1/ordering/orders', { body })),
    addLines: async (o: OrderSummary, lines: LineRequest[], deals?: DealRequest[]) =>
      unwrap(
        await api.POST('/api/v1/ordering/orders/{id}/lines', {
          ...order(o.id),
          headers: version(o.version),
          body: { lines, deals: deals ?? null },
        }),
      ),
    /** The price of a deal as chosen so far, from the pricing engine (FR-ORD-05); the POS never calculates it. */
    quoteDeal: async (body: Schemas['DealQuoteRequest']) =>
      unwrap(await api.POST('/api/v1/ordering/pos/deals/quote', { body })),
    updateLine: async (o: OrderSummary, lineId: string, body: Schemas['UpdateLineRequest']) =>
      unwrap(
        await api.PUT('/api/v1/ordering/orders/{id}/lines/{lineId}', {
          params: { path: { id: o.id, lineId } },
          headers: version(o.version),
          body,
        }),
      ),
    voidLine: async (o: OrderSummary, lineId: string, body: Schemas['VoidRequest']) =>
      unwrap(
        await api.POST('/api/v1/ordering/orders/{id}/lines/{lineId}/void', {
          params: { path: { id: o.id, lineId } },
          headers: version(o.version),
          body,
        }),
      ),
    addDiscount: async (o: OrderSummary, body: Schemas['DiscountRequest']) =>
      unwrap(
        await api.POST('/api/v1/ordering/orders/{id}/discounts', { ...order(o.id), headers: version(o.version), body }),
      ),
    /** A coupon code (FR-DSC-06, ADR 0043): HO checks it and holds one use; without HO, sync.ho_unavailable. */
    applyCoupon: async (o: OrderSummary, body: Schemas['CouponRequest']) =>
      unwrap(
        await api.POST('/api/v1/ordering/orders/{id}/coupons', { ...order(o.id), headers: version(o.version), body }),
      ),
    /** Takes a coupon off (ADR 0043); its use goes back to HO. */
    removeCoupon: async (o: OrderSummary, discountId: string) =>
      unwrap(
        await api.DELETE('/api/v1/ordering/orders/{id}/coupons/{discountId}', {
          params: { path: { id: o.id, discountId } },
          headers: version(o.version),
        }),
      ),
    /** The member's points as a discount on the order (ADR 0051): HO holds them within the club's limits; without HO, sync.ho_unavailable. */
    applyPoints: async (o: OrderSummary, body: Schemas['PointsRequest']) =>
      unwrap(
        await api.POST('/api/v1/ordering/orders/{id}/points', { ...order(o.id), headers: version(o.version), body }),
      ),
    /** Takes the points off the order (ADR 0051); they go back to the member. */
    removePoints: async (o: OrderSummary, discountId: string) =>
      unwrap(
        await api.DELETE('/api/v1/ordering/orders/{id}/points/{discountId}', {
          params: { path: { id: o.id, discountId } },
          headers: version(o.version),
        }),
      ),
    /** Members at HO (ADR 0044) by phone or card, masked; without HO, sync.ho_unavailable. */
    lookupMembers: async (query: string) =>
      unwrap(await api.POST('/api/v1/ordering/members/lookup', { body: { query } })),
    /** A member with the points available now, from HO. */
    getMember: async (accountId: string) =>
      unwrap(await api.GET('/api/v1/ordering/members/{accountId}', { params: { path: { accountId } } })),
    /** A new member, enrolled at HO with the consent they gave at the till. */
    enrolMember: async (body: MemberEnrolRequest) => unwrap(await api.POST('/api/v1/ordering/members', { body })),
    /** Puts a member on the order; HO confirms the account. */
    attachMember: async (o: OrderSummary, accountId: string) =>
      unwrap(
        await api.POST('/api/v1/ordering/orders/{id}/member', {
          ...order(o.id),
          headers: version(o.version),
          body: { accountId },
        }),
      ),
    /** Takes the member off the order; works offline. */
    detachMember: async (o: OrderSummary) =>
      unwrap(await api.DELETE('/api/v1/ordering/orders/{id}/member', { ...order(o.id), headers: version(o.version) })),
    removeDiscount: async (o: OrderSummary, discountId: string) =>
      unwrap(
        await api.DELETE('/api/v1/ordering/orders/{id}/discounts/{discountId}', {
          params: { path: { id: o.id, discountId } },
          headers: version(o.version),
        }),
      ),
    /** Which presets and charges can be used on the order (or one line) now (ADR 0039); the store answers offline. */
    eligibleAdjustments: async (id: string, lineId?: string | null) =>
      unwrap(
        await api.GET('/api/v1/ordering/orders/{id}/adjustments', {
          params: { path: { id }, query: { lineId: lineId ?? undefined } },
        }),
      ),
    /** Adds a charge; `value` is a rate or amount typed by the cashier, where the charge type allows it (amount_entry). */
    addCharge: async (
      o: OrderSummary,
      chargeTypeId: string,
      value: number | null = null,
      approvalId: string | null = null,
    ) =>
      unwrap(
        await api.POST('/api/v1/ordering/orders/{id}/charges', {
          ...order(o.id),
          headers: version(o.version),
          body: { chargeTypeId, value, approvalId },
        }),
      ),
    /** Takes a charge or fee off (FR-DSC-12); without store.charges.manage it needs a manager's `approvalId`. Not the delivery fee. */
    removeCharge: async (o: OrderSummary, chargeId: string, approvalId: string | null = null) =>
      unwrap(
        await api.DELETE('/api/v1/ordering/orders/{id}/charges/{chargeId}', {
          params: { path: { id: o.id, chargeId }, query: { approvalId: approvalId ?? undefined } },
          headers: version(o.version),
        }),
      ),
    updateDetails: async (o: OrderSummary, body: Schemas['UpdateDetailsRequest']) =>
      unwrap(
        await api.PUT('/api/v1/ordering/orders/{id}/details', { ...order(o.id), headers: version(o.version), body }),
      ),
    changeSaleType: async (o: OrderSummary, saleTypeId: string) =>
      unwrap(
        await api.PUT('/api/v1/ordering/orders/{id}/sale-type', {
          ...order(o.id),
          headers: version(o.version),
          body: { saleTypeId },
        }),
      ),
    send: async (o: OrderSummary) =>
      unwrap(await api.POST('/api/v1/ordering/orders/{id}/send', { ...order(o.id), headers: version(o.version) })),
    voidOrder: async (o: OrderSummary, body: Schemas['VoidRequest']) =>
      unwrap(
        await api.POST('/api/v1/ordering/orders/{id}/void', { ...order(o.id), headers: version(o.version), body }),
      ),
    /** Moves lines, part of a line or a share worth an amount to new bills at the same table (FR-ORD-28). */
    splitBill: async (o: OrderSummary, bills: SplitBillPart[], clientRequestKey: string) =>
      unwrap(
        await api.POST('/api/v1/ordering/orders/{id}/split', {
          ...order(o.id),
          headers: version(o.version),
          body: { bills, clientRequestKey },
        }),
      ),
    /** The open bills at the order's table, the oldest first (a split bill, FR-ORD-28). */
    tableBills: async (orderId: string) => unwrap(await api.GET('/api/v1/ordering/orders/{id}/bills', order(orderId))),

    /** Sets or clears the company buyer of a VAT e-invoice (FR-PAY-14). */
    updateInvoice: async (o: OrderSummary, invoice: InvoiceInfo | null) =>
      unwrap(
        await api.PUT('/api/v1/ordering/orders/{id}/invoice', {
          ...order(o.id),
          headers: version(o.version),
          body: { invoice },
        }),
      ),
    /**
     * The customer with this phone number or email and their saved addresses (FR-CUS-02): the store's own, else HO's (kept at
     * the store from then on, ADR 0080); null when nobody is known or HO can't be asked.
     */
    lookupCustomer: async (query: { phone?: string; email?: string }): Promise<KnownCustomer | null> => {
      const result = await api.GET('/api/v1/customers/lookup', { params: { query } });
      return result.response.status === 204 ? null : unwrap(result);
    },
    /** Up to five customers whose phone number contains these digits (at least five), for when only part is known. */
    searchCustomers: async (phone: string): Promise<KnownCustomer[]> =>
      unwrap(await api.GET('/api/v1/customers/search', { params: { query: { phone } } })),
    /** The store's own drivers, the most recently used first (FR-DLV-14). */
    deliveryDrivers: async () => unwrap(await api.GET('/api/v1/ordering/delivery-drivers')),
    /** Fees this store charged most on its own drivers' deliveries lately (up to four), to offer as one tap. */
    recentDeliveryFees: async () => unwrap(await api.GET('/api/v1/ordering/delivery-fees/recent')),
    createDriver: async (body: Schemas['DriverInput']) =>
      unwrap(await api.POST('/api/v1/ordering/delivery-drivers', { body })),
    updateDriver: async (d: DeliveryDriver, body: Schemas['DriverInput']) =>
      unwrap(
        await api.PUT('/api/v1/ordering/delivery-drivers/{id}', {
          params: { path: { id: d.id } },
          headers: version(d.version),
          body,
        }),
      ),
    removeDriver: async (id: string) =>
      unwrap(await api.DELETE('/api/v1/ordering/delivery-drivers/{id}', { params: { path: { id } } })),
    /**
     * Prints the bill on the terminal's receipt printer and marks the order "bill printed" (FR-ORD-27). The order
     * changes even when no printer is set up: `queued` and `reason` say what happened. Pressing it again is a reprint.
     */
    printBill: async (o: OrderSummary) =>
      unwrap(
        await api.POST('/api/v1/ordering/orders/{id}/print-bill', { ...order(o.id), headers: version(o.version) }),
      ),
    /**
     * Prints the final receipt of a paid order (FR-PAY-12, FR-PAY-15). The first print is the receipt, later ones are
     * recorded reprints; the order doesn't change. `queued` and `reason` say whether a printer got it.
     */
    printReceipt: async (orderId: string) =>
      unwrap(await api.POST('/api/v1/ordering/orders/{id}/print-receipt', order(orderId))),
    createDelivery: async (orderId: string, body: Schemas['CreateDeliveryRequest']) =>
      unwrap(await api.POST('/api/v1/ordering/orders/{id}/deliveries', { ...order(orderId), body })),
    /** The delivery services the cashier can choose for a sale type here (ADR 0025). */
    deliveryServices: async (saleTypeId: string) =>
      unwrap(await api.GET('/api/v1/ordering/delivery-services', { params: { query: { saleTypeId } } })),
    /** The provider's fee for this order, from head office; fails with sync.ho_unavailable when HO can't be reached. */
    quoteDelivery: async (orderId: string, deliveryServiceId: string) =>
      unwrap(
        await api.POST('/api/v1/ordering/orders/{id}/delivery-quote', {
          ...order(orderId),
          body: { deliveryServiceId },
        }),
      ),
    /** Asks the provider for a driver, through head office. Asked again it returns the same booking. */
    requestDriver: async (deliveryId: string) =>
      unwrap(
        await api.POST('/api/v1/ordering/deliveries/{id}/request-driver', { params: { path: { id: deliveryId } } }),
      ),
    cancelDelivery: async (delivery: Delivery, reason: string | null) =>
      unwrap(
        await api.POST('/api/v1/ordering/deliveries/{id}/cancel', {
          params: { path: { id: delivery.id } },
          headers: version(delivery.version),
          body: { reason },
        }),
      ),
    /** Cancels this delivery and starts another with a different service. */
    replaceDelivery: async (delivery: Delivery, reason: string | null, next: CreateDeliveryBody) =>
      unwrap(
        await api.POST('/api/v1/ordering/deliveries/{id}/replace', {
          params: { path: { id: delivery.id } },
          headers: version(delivery.version),
          body: { reason, next },
        }),
      ),
    refreshDelivery: async (deliveryId: string) =>
      unwrap(await api.POST('/api/v1/ordering/deliveries/{id}/refresh', { params: { path: { id: deliveryId } } })),
    deliveryStatus: async (delivery: Delivery, body: Schemas['DeliveryStatusRequest']) =>
      unwrap(
        await api.POST('/api/v1/ordering/deliveries/{id}/status', {
          params: { path: { id: delivery.id } },
          headers: version(delivery.version),
          body,
        }),
      ),

    recordPayment: async (orderId: string, body: Schemas['RecordPaymentRequest']) =>
      unwrap(await api.POST('/api/v1/payments/orders/{orderId}/payments', { params: { path: { orderId } }, body })),
    /** Starts a QR or wallet tender through HO's payment hub (ADR 0074); without HO, sync.ho_unavailable. */
    startGatewayPayment: async (orderId: string, body: Schemas['StartGatewayPaymentRequest']) =>
      unwrap(await api.POST('/api/v1/payments/orders/{orderId}/gateway', { params: { path: { orderId } }, body })),
    /** The QR payment now: its QR once the provider answered, and whether it is paid (then it is on the order). */
    getGatewayPayment: async (id: string) =>
      unwrap(await api.GET('/api/v1/payments/gateway/{id}', { params: { path: { id } } })),
    /** Check payment: HO asks the provider now. */
    checkGatewayPayment: async (id: string) =>
      unwrap(await api.POST('/api/v1/payments/gateway/{id}/check', { params: { path: { id } } })),
    /** Gives up on a QR not paid yet; money still paid later is refunded at HO. */
    cancelGatewayPayment: async (id: string) =>
      unwrap(await api.POST('/api/v1/payments/gateway/{id}/cancel', { params: { path: { id } } })),
    removePayment: async (paymentId: string) =>
      unwrap(await api.POST('/api/v1/payments/payments/{id}/remove', { params: { path: { id: paymentId } } })),
    refund: async (paymentId: string, body: Schemas['RefundRequest']) =>
      unwrap(await api.POST('/api/v1/payments/payments/{id}/refund', { params: { path: { id: paymentId } }, body })),

    /** A manager's single-use approval for one action on one record (FR-AUTH-07). */
    approve: async (body: {
      actionCode: string;
      targetType: string;
      targetId: string;
      staffCode: string;
      password: string;
    }) =>
      unwrap(
        await api.POST('/api/v1/identity/approvals', {
          body: { ...body, amount: null, reasonCodeId: null, note: null },
        }),
      ),
  };
}

export type OrdersApi = ReturnType<typeof createOrdersApi>;
